/**
 * pillSprite — runtime-генерация stretchable rounded-rect спрайта-подложки для
 * field-label pills (AK-299 / AK-303).
 *
 * ЗАЧЕМ. Подписи полей на /map должны выглядеть как pill-карточки (единый язык
 * со scope-чипами «Аргамак» и popup AK-295), а НЕ как голый haloed-текст.
 * Делать это HTML-маркерами на тысячах полей = перф-риск (white-canvas/freeze
 * зона 2026-06-25). Поэтому подложка рендерится В symbol-слое через
 * `icon-image` + `icon-text-fit`: один растягиваемый спрайт остаётся на GPU,
 * без HTML-оверлеев.
 *
 * КАК. MapLibre `addImage(id, img, {stretchX, stretchY, content, pixelRatio})`
 * умеет 9-slice растягивание: углы (скруглённые торцы pill) остаются чёткими,
 * растягивается только плоская середина под текст. `icon-text-fit: 'both'` в
 * layout слоя сообщает MapLibre, что спрайт надо подогнать под bbox текста.
 *
 * ⚠️ ТОКЕНЫ И ТЕМА (как AK-298). MapLibre НЕ резолвит `hsl(var(--token))` ни в
 * `addImage`-спрайте, ни в `text-color`. Значения токенов читаются из
 * **computed-стилей** через скрытый probe-элемент (см. `resolvePillColors`):
 * браузер раскрывает цепочку `var()` → `palette` → HSL в готовый `rgb(...)`.
 * При смене темы (`.dark` / `data-theme` на <html>) спрайт **переустанавливается**
 * — MutationObserver следит за атрибутами documentElement.
 *
 * Геометрия (design-lead AK-303): высота 22px, radius = full (height/2 = 11px),
 * padding гориз. 10px (закладывается через icon-text-fit-padding в registry),
 * фон `--popover`@0.96, бордер 1px `--border`, тень — лёгкая (пилюль много на
 * экране, не перегружать; рисуем мягкую тень прямо в спрайт).
 */
import type { Map as MLMap, StyleImageMetadata } from 'maplibre-gl';
import { MAP_SELECTION_YELLOW } from '@/lib/colorMaps/mapBrand';

/** ID спрайта-подложки, на который ссылаются pill-слои в registry.ts. */
export const PILL_SPRITE_ID = 'field-pill-bg';

/**
 * AK-590 — спрайт-подложка ВЫБРАННОГО поля: та же геометрия, бордер 2px
 * `MAP_SELECTION_YELLOW` (связка с жёлтым контуром `field-selected-highlight`).
 * Подменяется data-driven выражением `icon-image` (см. `pillIconImageExpression`),
 * поэтому отдельного слоя под выбранное поле НЕ нужно.
 */
export const PILL_SPRITE_SELECTED_ID = 'field-pill-bg-selected';

/**
 * ID pill-слоёв (registry.ts §12.e/§12.f). `installPillSprite` правит их
 * `text-field` при смене темы — registry даёт лишь light-fallback, потому что
 * MapLibre не резолвит `hsl(var(--token))` в статичном layout.
 *
 * AK-590: прежние `field-pill-number-area` + `field-pill-crop` СЛИТЫ в одну
 * пилюлю на поле (вариант B, approve founder 16.08). Два уровня детализации
 * задаются двумя ВЗАИМОИСКЛЮЧАЮЩИМИ по зуму слоями (зум-выражение в
 * `text-field` style spec'ом запрещено — только в фильтре):
 *   - `field-pill-compact` — «чип» на пороге поля (`min_zoom_for_label`),
 *     только номер;
 *   - `field-pill` — полная двухстрочная пилюля на `+1` зум.
 * На любом зуме поле рисует РОВНО ОДНУ пилюлю (было до трёх symbol'ов).
 */
export const FIELD_PILL_COMPACT_LAYER = 'field-pill-compact';
export const FIELD_PILL_LAYER = 'field-pill';

/** Оба pill-слоя — порядок не важен, используется для sync-проходов. */
export const FIELD_PILL_LAYERS = [FIELD_PILL_COMPACT_LAYER, FIELD_PILL_LAYER] as const;

/**
 * Sentinel «ничего не выбрано» для data-driven выражений выбора. Совпадает с
 * сентинелом фильтра `field-selected-highlight` (layerSyncEffect) — UUID-строка
 * никогда не даст такое значение.
 */
export const PILL_SELECTED_SENTINEL = '__none__';

/** sort-key выбранного поля: заведомо больше любой площади в га. */
const SELECTED_SORT_KEY = 1_000_000_000;

/** Высота pill в логических px (design-lead AK-303). */
const PILL_HEIGHT = 22;
/** Скругление = full → радиус = половина высоты. */
const PILL_RADIUS = PILL_HEIGHT / 2;
/**
 * Плоская растягиваемая зона по центру (между скруглёнными торцами). Должна
 * лежать строго между концом левого радиуса и началом правого, иначе 9-slice
 * размажет скругление. Берём 2px в середине — достаточно для линейного stretch.
 */
const STRETCH_W = 2;
/** Поля под тень, чтобы box-shadow не обрезался краем спрайта. */
const SHADOW_PAD = 3;
/**
 * Рендерим спрайт в 2× для чёткости на retina. MapLibre получает `pixelRatio`
 * и масштабирует обратно к логическим px.
 */
const SCALE = 2;

interface PillColors {
  /** Заливка pill — `--popover` (light: белый / dark: тёмный). */
  bg: string;
  /** Бордер — `--border`. */
  border: string;
  /** Текст номера/культуры — `--foreground`. */
  fg: string;
  /** Текст площади — `--muted-foreground`. */
  mutedFg: string;
}

const FALLBACK_LIGHT: PillColors = {
  bg: 'rgb(255, 255, 255)',
  border: 'rgb(231, 229, 228)',
  fg: 'rgb(28, 25, 23)',
  mutedFg: 'rgb(120, 113, 108)',
};

/**
 * Резолвит цвета токенов через скрытый probe-элемент. `getComputedStyle` на
 * custom property вернул бы НЕразвёрнутый `var(--palette-…)`; поэтому ставим
 * токены как реальные свойства (`color` / `border-color`) и читаем уже
 * вычисленный браузером `rgb(...)`. Каждый токен — отдельный probe (одно
 * `color`-свойство на элемент), чтобы не путать значения.
 */
function readComputedColor(cssExpr: string, property: 'color' | 'border-color', fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const probe = document.createElement('span');
  probe.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:0;height:0;${property}:${cssExpr};`;
  document.body.appendChild(probe);
  try {
    const cs = getComputedStyle(probe);
    return (property === 'color' ? cs.color : cs.borderTopColor) || fallback;
  } catch {
    return fallback;
  } finally {
    probe.remove();
  }
}

function resolvePillColors(): PillColors {
  return {
    bg: readComputedColor('hsl(var(--popover))', 'color', FALLBACK_LIGHT.bg),
    border: readComputedColor('hsl(var(--border))', 'border-color', FALLBACK_LIGHT.border),
    fg: readComputedColor('hsl(var(--foreground))', 'color', FALLBACK_LIGHT.fg),
    mutedFg: readComputedColor('hsl(var(--muted-foreground))', 'color', FALLBACK_LIGHT.mutedFg),
  };
}

/** Применяет alpha к `rgb(...)`/`rgba(...)` строке, возвращает `rgba(...)`. */
function withAlpha(color: string, alpha: number): string {
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (!m) return color;
  const parts = m[1].split(',').map((s) => s.trim());
  const [r, g, b] = parts;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function get2dCtx(w: number, h: number): {
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  getImageData: () => ImageData;
} {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('OffscreenCanvas 2d unavailable');
    return { ctx, getImageData: () => ctx.getImageData(0, 0, w, h) };
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d unavailable');
  return { ctx, getImageData: () => ctx.getImageData(0, 0, w, h) };
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Генерирует ImageData спрайта + 9-slice метаданные. Логические размеры:
 * ширина = 2·radius + STRETCH_W + 2·SHADOW_PAD, высота = PILL_HEIGHT + 2·SHADOW_PAD.
 * Растровые — те же ×SCALE.
 */
function buildPillImage(
  colors: PillColors,
  variant: { borderColor?: string; borderWidth?: number } = {},
): { data: ImageData; meta: StyleImageMetadata } {
  const borderColor = variant.borderColor ?? colors.border;
  const borderWidth = variant.borderWidth ?? 1;
  const logicalW = PILL_RADIUS * 2 + STRETCH_W + SHADOW_PAD * 2;
  const logicalH = PILL_HEIGHT + SHADOW_PAD * 2;
  const w = Math.round(logicalW * SCALE);
  const h = Math.round(logicalH * SCALE);
  const { ctx, getImageData } = get2dCtx(w, h);

  ctx.clearRect(0, 0, w, h);
  ctx.scale(SCALE, SCALE);

  const rectX = SHADOW_PAD;
  const rectY = SHADOW_PAD;
  const rectW = PILL_RADIUS * 2 + STRETCH_W;
  const rectH = PILL_HEIGHT;

  // Мягкая тень (лёгкая — пилюль много, не перегружаем).
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
  ctx.shadowBlur = 2.5;
  ctx.shadowOffsetY = 1;
  roundedRectPath(ctx, rectX, rectY, rectW, rectH, PILL_RADIUS);
  ctx.fillStyle = withAlpha(colors.bg, 0.96);
  ctx.fill();
  ctx.restore();

  // Бордер (поверх заливки, без тени). 1px `--border` для обычной пилюли,
  // 2px `MAP_SELECTION_YELLOW` для выбранного поля (AK-590).
  const inset = borderWidth / 2;
  roundedRectPath(
    ctx,
    rectX + inset,
    rectY + inset,
    rectW - borderWidth,
    rectH - borderWidth,
    PILL_RADIUS - inset,
  );
  ctx.lineWidth = borderWidth;
  ctx.strokeStyle = borderColor;
  ctx.stroke();

  // 9-slice зоны в РАСТРОВЫХ px (×SCALE). Stretchable — узкая плоская середина
  // между скруглёнными торцами. content — та же зона по X (текст ложится в
  // растягиваемую середину), по Y — внутренняя высота pill без тени.
  const stretchStart = Math.round((SHADOW_PAD + PILL_RADIUS) * SCALE);
  const stretchEnd = Math.round((SHADOW_PAD + PILL_RADIUS + STRETCH_W) * SCALE);
  const contentTop = Math.round(SHADOW_PAD * SCALE);
  const contentBottom = Math.round((SHADOW_PAD + PILL_HEIGHT) * SCALE);

  const meta: StyleImageMetadata = {
    pixelRatio: SCALE,
    // Цветной спрайт (не SDF) — рисуем фактические rgba пиксели pill.
    sdf: false,
    stretchX: [[stretchStart, stretchEnd]],
    stretchY: [[contentTop, contentBottom]],
    content: [stretchStart, contentTop, stretchEnd, contentBottom],
  };

  return { data: getImageData(), meta };
}

/**
 * Видимость секций pill — управляется тогглами `state.labels.*`
 * (AK-297 founder UAT 2026-06-28; AK-590 — все четыре в ОДНОЙ пилюле).
 */
export interface NumberAreaToggles {
  /** Показывать номер/название поля (`state.labels.number`). */
  number: boolean;
  /** Показывать площадь (`state.labels.area`). */
  area: boolean;
  /** Показывать название культуры — вторая строка (`state.labels.crop`). */
  crop?: boolean;
  /** Показывать иконку культуры — вторая строка (`state.labels.icon`). */
  icon?: boolean;
}

/**
 * AK-590 — площадь ЦЕЛЫМИ гектарами, считается НА ФРОНТЕ из числового
 * `area_ha` (без миграции MV): `round(area_ha) + ' га'`.
 *
 * Почему не `label_area` из MVT: строка MV формируется в БД и её формат менялся
 * между миграциями (десятые в легаси-тайлах, кэш тайлов — сутки). Считая на
 * фронте, мы гарантируем «47 га» вне зависимости от возраста тайла. Если
 * `area_ha` в тайле нет (совсем старый тайл) — падаем на `label_area`.
 */
const AREA_WHOLE_HA_EXPR: unknown[] = [
  'case',
  ['has', 'area_ha'],
  ['concat', ['to-string', ['round', ['number', ['get', 'area_ha'], 0]]], ' га'],
  ['coalesce', ['get', 'label_area'], ''],
];

/**
 * AK-590 — иконка культуры инлайном во ВТОРОЙ строке пилюли (`format` умеет
 * image-секции), а не отдельным symbol-слоем. Это и убирает третий symbol на
 * поле, и ставит иконку рядом с названием культуры (уточнение founder 16.08).
 *
 * ⚠️ Побочный фикс: MVT отдаёт `icon_id = 'crop-<uuid>'` (MV
 * `field_visual_attrs`), а `cropPatterns.registerAllCropImages` регистрирует
 * изображения под `crop-icon-<uuid>` — прежний слой `field-label-icon` c
 * `icon-image: ['get','icon_id']` ссылался на НЕсуществующий образ и молча не
 * рисовал ничего (`styleimagemissing`-handler тоже фильтрует по префиксу
 * `crop-icon-`). Приводим id к реальному префиксу выражением, без миграции MV.
 */
const CROP_ICON_IMAGE_EXPR: unknown[] = [
  'image',
  ['concat', 'crop-icon-', ['slice', ['coalesce', ['get', 'icon_id'], 'crop-'], 5]],
];

const CROP_NAME_EXPR: unknown[] = ['coalesce', ['get', 'label_crop'], ''];

/**
 * AK-590 — строит `format`-выражение ЕДИНОЙ пилюли поля.
 *
 * Утверждённый founder'ом состав (вариант B + уточнения 16.08):
 *   строка 1: «{номер}   {площадь целыми га}» — номер `--foreground`,
 *             площадь `--muted-foreground`, мельче (font-scale 0.92);
 *   строка 2: «{иконка культуры} {культура}» — только когда включены
 *             соответствующие тогглы.
 *
 * Два уровня детализации (`compact`):
 *   - `compact: true`  → чип «только номер» (порог `min_zoom_for_label`);
 *   - `compact: false` → полная двухстрочная пилюля (`min_zoom_for_label + 1`).
 *
 * Compact-fallback: если «Номер поля» выключен, чип показывает первое, что
 * включено (площадь → иконка → культура) — иначе пилюля была бы пустой
 * плашкой (регресс AK-101 «тумблер включён, а ничего не видно»).
 *
 * Цвета — параметры (theme-aware): MapLibre не резолвит `hsl(var(--token))`,
 * поэтому передаём готовые `rgb(...)`. Возвращается `unknown[]`, чтобы лечь в
 * layout `text-field` (LayerConfig.layout = Record<string, unknown>).
 */
export function buildPillTextField(
  fg: string,
  mutedFg: string,
  toggles: NumberAreaToggles = { number: true, area: true, crop: true, icon: true },
  opts: { compact?: boolean } = {},
): unknown[] {
  const sections: unknown[] = ['format'];
  const showNumber = toggles.number;
  const showArea = toggles.area;
  const showCrop = toggles.crop ?? false;
  const showIcon = toggles.icon ?? false;

  const pushNumber = () =>
    sections.push(['get', 'label_number'], { 'text-color': fg, 'font-scale': 1.0 });
  // Площадь — `--muted-foreground` + чуть мельче (≈11px при base 12). Дизайн
  // просит `--font-mono`, но glyph-стек карты моноширинный fontstack не отдаёт
  // — мину достигаем цветом + размером (follow-up: свой glyph-сервер).
  const pushArea = () =>
    sections.push(AREA_WHOLE_HA_EXPR, { 'text-color': mutedFg, 'font-scale': 0.92 });
  const pushIcon = () => sections.push(CROP_ICON_IMAGE_EXPR, {});
  const pushCrop = () => sections.push(CROP_NAME_EXPR, { 'text-color': fg, 'font-scale': 0.92 });

  if (opts.compact) {
    // Чип: одна короткая секция, которая влезает в поле на его пороге.
    if (showNumber) pushNumber();
    else if (showArea) pushArea();
    else if (showIcon) pushIcon();
    else if (showCrop) pushCrop();
  } else {
    if (showNumber) pushNumber();
    // Разделитель только между обеими секциями первой строки — иначе
    // одиночная секция получила бы висячий отступ.
    if (showNumber && showArea) sections.push('   ', {});
    if (showArea) pushArea();

    const hasLine2 = showCrop || showIcon;
    if (hasLine2) {
      // Перенос строки — отдельной секцией, чтобы не склеивать с цветными.
      if (sections.length > 1) sections.push('\n', {});
      if (showIcon) pushIcon();
      if (showIcon && showCrop) sections.push(' ', {});
      if (showCrop) pushCrop();
    }
  }

  // Всё OFF — слой скрыт предикатом, но возвращаем валидный пустой format,
  // чтобы setLayoutProperty не упал, если его всё же позовут в этом состоянии.
  if (sections.length === 1) {
    sections.push('', {});
  }

  return sections;
}

/**
 * AK-590 §5.2 — подпись ВЫБРАННОГО поля всегда поверх остальных + жёлтый
 * бордер. Реализовано БЕЗ отдельного слоя: data-driven `symbol-sort-key`
 * (выбранное поле получает заведомо максимальный ключ) и data-driven
 * `icon-image` (подменяет подложку на жёлтую).
 *
 * ⚠️ Порядок отрисовки при `*-allow-overlap: true` (freeze-инвариант, снимать
 * нельзя): по style spec поверх рисуется фича с БОЛЬШИМ `symbol-sort-key`.
 * Поэтому базовый ключ = `area_ha` (крупное поле перекрывает мелкие дольки —
 * прежнее `['-', 0, area_ha]` делало РОВНО наоборот, вопреки своему же
 * комментарию; это и был founder-кейс «мелкая долька затёрла MA1-078.1»).
 */
export function pillSortKeyExpression(selectedFieldId: string | null): unknown[] {
  return [
    'case',
    ['==', ['get', 'id'], selectedFieldId ?? PILL_SELECTED_SENTINEL],
    SELECTED_SORT_KEY,
    ['number', ['get', 'area_ha'], 0],
  ];
}

/** Подложка пилюли: жёлтая для выбранного поля, нейтральная для остальных. */
export function pillIconImageExpression(selectedFieldId: string | null): unknown[] {
  return [
    'case',
    ['==', ['get', 'id'], selectedFieldId ?? PILL_SELECTED_SENTINEL],
    PILL_SPRITE_SELECTED_ID,
    PILL_SPRITE_ID,
  ];
}

/**
 * Источник текущего состояния тогглов для theme-sync. Спрайт-модуль не должен
 * импортировать store (циклы / тестируемость), поэтому актуальные тогглы
 * прокидываются геттером из эффекта. По умолчанию — обе секции видны (легаси).
 */
export type NumberAreaTogglesGetter = () => NumberAreaToggles;

const DEFAULT_TOGGLES_GETTER: NumberAreaTogglesGetter = () => ({
  number: true,
  area: true,
  crop: true,
  icon: true,
});

/**
 * (Пере)регистрирует спрайт-подложку pill под текущую тему и синхронизирует
 * цвета текста pill-слоёв (registry даёт лишь light-fallback). Идемпотентно:
 * если спрайт есть — снимает и ставит заново. Требует загруженного style.
 *
 * AK-297: `text-field` pill пересобирается с УЧЁТОМ тогглов (`getToggles`),
 * чтобы theme-sync не затирал toggle-состояние полным concat'ом.
 * AK-590: спрайтов два (обычный + жёлтый «выбранное поле»), слоёв тоже два
 * (чип + полная пилюля) — синхронизируем оба.
 */
export function registerPillSprite(
  map: MLMap,
  getToggles: NumberAreaTogglesGetter = DEFAULT_TOGGLES_GETTER,
): void {
  let colors: PillColors;
  try {
    colors = resolvePillColors();
  } catch {
    colors = FALLBACK_LIGHT;
  }
  for (const [id, variant] of [
    [PILL_SPRITE_ID, {}],
    [PILL_SPRITE_SELECTED_ID, { borderColor: MAP_SELECTION_YELLOW, borderWidth: 2 }],
  ] as const) {
    try {
      const { data, meta } = buildPillImage(colors, variant);
      if (map.hasImage(id)) {
        map.removeImage(id);
      }
      map.addImage(id, data, meta);
    } catch (e) {
      if (import.meta.env.DEV) console.warn(`[pillSprite] addImage(${id}) failed`, e);
    }
  }

  // Theme-aware текст pill-слоёв (могут ещё не существовать на первом apply —
  // тогда LayerManager поставит registry-light-дефолт, а следующий apply на
  // load/смене темы их догонит).
  applyPillTextFields(map, colors, getToggles());
}

/** Ставит `text-field` обоим pill-слоям (чип — compact, полная — двухстрочная). */
function applyPillTextFields(map: MLMap, colors: PillColors, toggles: NumberAreaToggles): void {
  try {
    if (map.getLayer(FIELD_PILL_COMPACT_LAYER)) {
      map.setLayoutProperty(
        FIELD_PILL_COMPACT_LAYER,
        'text-field',
        buildPillTextField(colors.fg, colors.mutedFg, toggles, { compact: true }),
      );
    }
    if (map.getLayer(FIELD_PILL_LAYER)) {
      map.setLayoutProperty(
        FIELD_PILL_LAYER,
        'text-field',
        buildPillTextField(colors.fg, colors.mutedFg, toggles),
      );
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[pillSprite] layer text sync failed', e);
  }
}

/**
 * Пересобирает `text-field` обеих пилюль под текущие тогглы, сохраняя
 * theme-aware цвета. Вызывается из эффекта при смене `labels.*` (AK-297 /
 * AK-590) — без перерегистрации спрайтов. No-op если слои ещё не созданы
 * (LayerManager догонит на следующем apply).
 */
export function syncNumberAreaTextField(map: MLMap, toggles: NumberAreaToggles): void {
  if (!map.getLayer(FIELD_PILL_COMPACT_LAYER) && !map.getLayer(FIELD_PILL_LAYER)) return;
  let colors: PillColors;
  try {
    colors = resolvePillColors();
  } catch {
    colors = FALLBACK_LIGHT;
  }
  applyPillTextFields(map, colors, toggles);
}

/**
 * AK-590 — синхронизирует «выбранное поле» на обеих пилюлях: sort-key (всегда
 * поверх) + подложка с жёлтым бордером. Вызывается из `useLayerSyncEffect`
 * вместе с фильтром `field-selected-highlight` — один источник правды по
 * выделению. No-op если слоёв ещё нет.
 */
export function syncPillSelection(map: MLMap, selectedFieldId: string | null): void {
  const sortKey = pillSortKeyExpression(selectedFieldId);
  const iconImage = pillIconImageExpression(selectedFieldId);
  for (const layerId of FIELD_PILL_LAYERS) {
    if (!map.getLayer(layerId)) continue;
    try {
      map.setLayoutProperty(layerId, 'symbol-sort-key', sortKey);
      map.setLayoutProperty(layerId, 'icon-image', iconImage);
    } catch (e) {
      if (import.meta.env.DEV) console.warn(`[pillSprite] selection sync failed (${layerId})`, e);
    }
  }
}

/**
 * Полный side-effect: регистрирует спрайт после `load` и переустанавливает при
 * смене темы (наблюдая class/`data-theme` на <html>). Возвращает cleanup.
 *
 * Используется из `usePillSpriteEffect` (effects/pillSpriteEffect.ts).
 */
export function installPillSprite(
  map: MLMap,
  getToggles: NumberAreaTogglesGetter = DEFAULT_TOGGLES_GETTER,
): () => void {
  let disposed = false;

  const apply = () => {
    if (disposed) return;
    registerPillSprite(map, getToggles);
  };

  const run = () => {
    if (typeof map.isStyleLoaded === 'function' && map.isStyleLoaded()) {
      apply();
    } else {
      map.once('load', apply);
    }
  };
  run();

  // Pill-слои создаются LayerManager.init() в отдельном `load`-обработчике —
  // порядок относительно нашего apply не гарантирован. `idle` наступает после
  // того как все load-обработчики отработали и слои уже добавлены, поэтому
  // дополнительный apply на первом idle гарантирует корректный text-color на
  // первой загрузке (важно для dark-mode-on-load, где registry-дефолт светлый).
  const applyOnceIdle = () => {
    if (disposed) return;
    apply();
  };
  map.once('idle', applyOnceIdle);

  // Смена темы → перерисовать спрайт с новыми computed-токенами.
  let observer: MutationObserver | null = null;
  if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
    observer = new MutationObserver(() => apply());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
  }

  return () => {
    disposed = true;
    observer?.disconnect();
    map.off('load', apply);
    map.off('idle', applyOnceIdle);
  };
}
